// src/pages/infografia/InfografiaGeneratorPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaDownload, FaCopy, FaTrash, FaTimes, FaRulerCombined } from 'react-icons/fa';
import useAuthStore from '../../store/authStore';
import { get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import InfografiaCanvas from '../../components/infografia/canvas/InfografiaCanvas';
import TemplateSelector from '../../components/infografia/templates/TemplateSelector';
import Toolbar from '../../components/infografia/toolbar/Toolbar';
import PropertiesPanel from '../../components/infografia/properties/PropertiesPanel';
import BackgroundProperties from '../../components/infografia/properties/BackgroundProperties';
import PreviewModal from '../../components/infografia/preview/PreviewModal';
import ImageSearchModal from '../../components/infografia/modals/ImageSearchModal';
import IconSelectorModal from '../../components/infografia/modals/IconSelectorModal';
import ShapeSelectorModal from '../../components/infografia/modals/ShapeSelectorModal';
import DimensionSelectorModal from '../../components/infografia/modals/DimensionSelectorModal';
import infografiaService from '../../helpers/infografia/infografiaService';

const InfografiaGeneratorPage = () => {
    const navigate = useNavigate();
    const { marcaId } = useParams();
    const { user } = useAuthStore();

    const [loading, setLoading] = useState(true);
    const [marca, setMarca] = useState(null);
    const [infografias, setInfografias] = useState([]);

    // Estados del editor
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [canvasData, setCanvasData] = useState(null);
    const [selectedElement, setSelectedElement] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [currentInfografiaId, setCurrentInfografiaId] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showImageSearch, setShowImageSearch] = useState(false);
    const [showIconSelector, setShowIconSelector] = useState(false);
    const [showShapeSelector, setShowShapeSelector] = useState(false);
    const [showDimensionSelector, setShowDimensionSelector] = useState(false);
    const [pendingImageElement, setPendingImageElement] = useState(null);
    const [currentZoom, setCurrentZoom] = useState(0.3);
    const [fabricCanvas, setFabricCanvas] = useState(null);
    const [prepareForExport, setPrepareForExport] = useState(null);
    const [previewInfografia, setPreviewInfografia] = useState(null);
    const [editingInfografia, setEditingInfografia] = useState(null);

    // Estados de autoguardado
    const [autoSaveTimer, setAutoSaveTimer] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (user && marcaId) {
            fetchData();
        }
    }, [user, marcaId]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Cargar datos de marca
            const marcaData = await get(`marcas/${marcaId}`);
            setMarca(marcaData);

            // Cargar infografías existentes
            const infografiasData = await infografiaService.getInfografias(marcaId);
            setInfografias(infografiasData?.infografias || infografiasData || []);
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudieron cargar los datos',
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Autoguardado cada 30 segundos
    useEffect(() => {
        if (isDirty && canvasData && currentInfografiaId) {
            const timer = setTimeout(() => {
                handleAutoSave();
            }, 30000); // 30 segundos

            setAutoSaveTimer(timer);

            return () => {
                if (timer) clearTimeout(timer);
            };
        }
    }, [isDirty, canvasData, currentInfografiaId]);

    const handleTemplateSelect = async (template) => {
        try {
            setSelectedTemplate(template);

            // Caso especial: Template "Personalizado" o "custom"
            // Debe abrir selector de dimensiones en lugar de crear inmediatamente
            const templateCategoria = template.categoria || template.id;
            if (templateCategoria === 'custom') {
                setShowDimensionSelector(true);
                return; // No crear todavía, esperar a que el usuario seleccione dimensiones
            }

            // Determinar si el template viene del backend o es hardcodeado
            // Backend templates tienen _id que es un ObjectId (24 caracteres hexadecimales)
            // Hardcoded templates solo tienen 'id' o no tienen _id
            const templateId = template._id || template.id;
            const templateName = template.nombre || template.name;

            // Validar si _id existe Y es un ObjectId válido (24 caracteres hex)
            const isValidObjectId = template._id && /^[0-9a-fA-F]{24}$/.test(template._id);
            const isBackendTemplate = isValidObjectId;

            // Si es un template del backend (tiene _id válido de MongoDB)
            if (isBackendTemplate) {
                const response = await infografiaService.crearDesdeTemplate(
                    template._id, // Este es el ObjectId del backend
                    marcaId,
                    {
                        nombre: `Infografía ${templateName}`,
                        descripcion: template.descripcion || template.description
                    }
                );

                const infografia = response?.infografia || response;
                setCurrentInfografiaId(infografia._id);
                setCanvasData({
                    elementos: infografia.elementos || [],
                    fondo: infografia.fondo || { tipo: 'color', valor: { color: '#FFFFFF' } },
                    dimensiones: infografia.dimensiones || template.dimensiones
                });
                setIsDirty(false);
            } else {
                // Crear nueva infografía con template hardcodeado (fallback)
                const nuevaInfografia = {
                    marcaId,
                    nombre: `Infografía ${templateName}`,
                    template: 'custom', // Siempre 'custom' para templates hardcodeados
                    dimensiones: template.dimensiones || {
                        ancho: 2480,
                        alto: 3508,
                        unidad: 'px',
                        dpi: 300
                    },
                    elementos: template.elementos || [],
                    fondo: template.fondo || { tipo: 'color', valor: { color: '#FFFFFF' } },
                    coloresCorporativos: {
                        primario: marca?.colorPrimario || '#3B82F6',
                        secundario: marca?.colorSecundario || '#1D4ED8',
                        texto: '#111827',
                        fondo: '#FFFFFF'
                    },
                    esBorrador: true
                };

                const response = await infografiaService.createInfografia(nuevaInfografia);
                const infografia = response?.infografia || response;
                setCurrentInfografiaId(infografia._id);
                setCanvasData({
                    elementos: infografia.elementos || [],
                    fondo: infografia.fondo || { tipo: 'color', valor: { color: '#FFFFFF' } },
                    dimensiones: infografia.dimensiones || nuevaInfografia.dimensiones
                });
                setIsDirty(false);
            }
        } catch (error) {
            console.error('Error creando infografía:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo crear la infografía',
                icon: 'error'
            });
        }
    };

    const handleCanvasChange = (newCanvasData) => {
        setCanvasData(newCanvasData);
        setIsDirty(true);
    };

    const handleElementSelect = (element) => {
        setSelectedElement(element);
    };

    const handleElementUpdate = (elementId, properties) => {
        // Manejar acciones de capa
        if (properties.layerAction) {
            handleLayerAction(elementId, properties.layerAction);
            return;
        }

        const updatedElements = canvasData.elementos.map(el => {
            if (el.id === elementId) {
                const updated = { ...el, ...properties };
                return updated;
            }
            return el;
        });

        const newCanvasData = {
            ...canvasData,
            elementos: updatedElements
        };

        setCanvasData(newCanvasData);
        setIsDirty(true);

        // Actualizar elemento seleccionado si es el mismo - usar el elemento actualizado de la lista
        if (selectedElement && selectedElement.id === elementId) {
            const updatedSelected = updatedElements.find(el => el.id === elementId);
            if (updatedSelected) {
                setSelectedElement(updatedSelected);
            }
        }
    };

    const handleDeleteElement = (elementId) => {

        const updatedElements = canvasData.elementos.filter(el => el.id !== elementId);


        setCanvasData({
            ...canvasData,
            elementos: updatedElements
        });
        setIsDirty(true);

        // Si el elemento eliminado estaba seleccionado, deseleccionar
        if (selectedElement && selectedElement.id === elementId) {
            setSelectedElement(null);
        }
    };

    const handleAutoSave = async () => {
        if (!currentInfografiaId || !canvasData || isSaving) return;

        try {
            setIsSaving(true);
            await infografiaService.updateInfografia(currentInfografiaId, {
                elementos: canvasData.elementos,
                fondo: canvasData.fondo,
                dimensiones: canvasData.dimensiones
            });
            setIsDirty(false);
        } catch (error) {
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        if (!currentInfografiaId || !canvasData) return;

        try {
            setIsSaving(true);
            await infografiaService.updateInfografia(currentInfografiaId, {
                elementos: canvasData.elementos,
                fondo: canvasData.fondo,
                dimensiones: canvasData.dimensiones,
                esBorrador: false
            });
            setIsDirty(false);

            Swal.fire({
                title: '¡Guardado!',
                text: 'La infografía se ha guardado correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            // Recargar lista
            await fetchData();
        } catch (error) {
            console.error('Error guardando infografía:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo guardar la infografía',
                icon: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleExport = (format) => {
        setShowPreview(true);
        // La exportación real se maneja en PreviewModal
    };

    const handleDuplicate = async () => {
        if (!currentInfografiaId) return;

        try {
            const response = await infografiaService.duplicateInfografia(currentInfografiaId);
            Swal.fire({
                title: '¡Duplicado!',
                text: 'La infografía se ha duplicado correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
            await fetchData();
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo duplicar la infografía',
                icon: 'error'
            });
        }
    };

    const handleLayerAction = (elementId, action) => {
        if (!canvasData || !canvasData.elementos) return;

        const elementIndex = canvasData.elementos.findIndex(el => el.id === elementId);
        if (elementIndex === -1) return;

        const updatedElements = [...canvasData.elementos];
        const [element] = updatedElements.splice(elementIndex, 1);

        let newIndex = elementIndex;

        switch (action) {
            case 'toFront':
                // Mover al final del array (será el elemento más al frente)
                newIndex = updatedElements.length;
                break;
            case 'forward':
                // Subir una posición
                newIndex = Math.min(elementIndex + 1, updatedElements.length);
                break;
            case 'backward':
                // Bajar una posición
                newIndex = Math.max(elementIndex - 1, 0);
                break;
            case 'toBack':
                // Mover al inicio del array (será el elemento más al fondo)
                newIndex = 0;
                break;
            default:
                return;
        }

        updatedElements.splice(newIndex, 0, element);

        // Actualizar zIndex de todos los elementos según su nueva posición
        const elementsWithUpdatedZIndex = updatedElements.map((el, idx) => ({
            ...el,
            zIndex: idx
        }));

        setCanvasData({
            ...canvasData,
            elementos: elementsWithUpdatedZIndex
        });
        setIsDirty(true);
    };

    const handleDelete = async () => {
        if (!currentInfografiaId) return;

        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await infografiaService.deleteInfografia(currentInfografiaId);
                Swal.fire({
                    title: '¡Eliminado!',
                    text: 'La infografía se ha eliminado correctamente',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate(`/marcas/${marcaId}/personalizacion`);
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo eliminar la infografía',
                    icon: 'error'
                });
            }
        }
    };

    const handleDeleteFromList = async (infografiaId, event) => {
        event.stopPropagation(); // Evitar que se abra la infografía

        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await infografiaService.deleteInfografia(infografiaId);
                Swal.fire({
                    title: '¡Eliminado!',
                    text: 'La infografía se ha eliminado correctamente',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                await fetchData();
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo eliminar la infografía',
                    icon: 'error'
                });
            }
        }
    };

    const handleEditInfoFromList = async (infografia, event) => {
        event.stopPropagation(); // Evitar que se abra la infografía
        setEditingInfografia(infografia);
    };

    const handleSaveInfo = async () => {
        if (!editingInfografia) return;

        try {
            await infografiaService.updateInfografia(editingInfografia._id, {
                nombre: editingInfografia.nombre,
                descripcion: editingInfografia.descripcion
            });

            Swal.fire({
                title: '¡Actualizado!',
                text: 'La información se ha guardado correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            setEditingInfografia(null);
            await fetchData();
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo actualizar la información',
                icon: 'error'
            });
        }
    };

    const handlePreviewFromList = async (infografiaId, event) => {
        event.stopPropagation(); // Evitar que se abra la infografía

        try {
            const response = await infografiaService.getInfografia(infografiaId);
            const inf = response?.infografia || response;
            setPreviewInfografia(inf);
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo cargar la preview',
                icon: 'error'
            });
        }
    };

    const handleOpenEditor = async (infografiaId) => {
        try {
            const response = await infografiaService.getInfografia(infografiaId);
            const inf = response?.infografia || response;
            setCurrentInfografiaId(inf._id);
            setCanvasData({
                elementos: inf.elementos || [],
                fondo: inf.fondo || { tipo: 'color', valor: { color: '#FFFFFF' } },
                dimensiones: inf.dimensiones
            });
            setSelectedTemplate({ id: inf.template, name: inf.template });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo cargar la infografía',
                icon: 'error'
            });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando...</p>
                </div>
            </div>
        );
    }

    // Si no hay template seleccionado, mostrar selector
    if (!selectedTemplate && !currentInfografiaId) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-6">
                        <h1 className="text-3xl font-bold text-gray-900">Generador de Infografías</h1>
                        <p className="text-gray-600 mt-2">Crea carteles e infografías personalizadas para tu tienda</p>
                    </div>

                    {/* Templates primero */}
                    <TemplateSelector
                        onSelect={handleTemplateSelect}
                        marca={marca}
                    />

                    {/* Lista de infografías existentes */}
                    {infografias.length > 0 && (
                        <div className="mt-12">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Infografías Guardadas</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {infografias.map((infografia) => (
                                    <div
                                        key={infografia._id}
                                        className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden"
                                    >
                                        {/* Información */}
                                        <div className="p-4">
                                            <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">{infografia.nombre}</h3>
                                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{infografia.descripcion || 'Sin descripción'}</p>

                                            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                                                <span className="bg-gray-100 px-2 py-1 rounded">{infografia.template}</span>
                                                <span className={`px-2 py-1 rounded ${infografia.esBorrador ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                                    {infografia.esBorrador ? 'Borrador' : 'Finalizado'}
                                                </span>
                                            </div>

                                            {/* Botones de acción */}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleOpenEditor(infografia._id)}
                                                    className="flex-1 px-3 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    onClick={(e) => handlePreviewFromList(infografia._id, e)}
                                                    className="px-3 py-2 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors"
                                                    title="Ver Preview"
                                                >
                                                    👁️
                                                </button>
                                                <button
                                                    onClick={(e) => handleEditInfoFromList(infografia, e)}
                                                    className="px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors"
                                                    title="Editar Información"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    onClick={(e) => handleDeleteFromList(infografia._id, e)}
                                                    className="px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                                                    title="Eliminar"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal de edición de información */}
                {editingInfografia && (
                    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Editar Información
                            </h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre
                                    </label>
                                    <input
                                        type="text"
                                        value={editingInfografia.nombre}
                                        onChange={(e) => setEditingInfografia({
                                            ...editingInfografia,
                                            nombre: e.target.value
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Nombre de la infografía"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Descripción
                                    </label>
                                    <textarea
                                        value={editingInfografia.descripcion || ''}
                                        onChange={(e) => setEditingInfografia({
                                            ...editingInfografia,
                                            descripcion: e.target.value
                                        })}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Descripción de la infografía"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2 mt-6">
                                <button
                                    onClick={() => setEditingInfografia(null)}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSaveInfo}
                                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                >
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de preview */}
                {previewInfografia && (
                    <PreviewModal
                        canvasData={{
                            elementos: previewInfografia.elementos || [],
                            fondo: previewInfografia.fondo || { tipo: 'color', valor: { color: '#FFFFFF' } },
                            dimensiones: previewInfografia.dimensiones
                        }}
                        marca={marca}
                        onClose={() => setPreviewInfografia(null)}
                        fabricCanvas={null}
                        prepareForExport={null}
                        isPreviewOnly={true}
                    />
                )}
            </div>
        );
    }

    // Editor activo
    return (
        <div className="h-screen flex flex-col bg-gray-100">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => {
                            setSelectedTemplate(null);
                            setCurrentInfografiaId(null);
                            setCanvasData(null);
                            setSelectedElement(null);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                    >
                        <FaArrowLeft />
                    </button>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Editor de Infografía</h1>
                        {isSaving && (
                            <p className="text-xs text-gray-500">Guardando...</p>
                        )}
                        {isDirty && !isSaving && (
                            <p className="text-xs text-yellow-600">Cambios sin guardar</p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                    >
                        <FaSave />
                        Guardar
                    </button>
                    <button
                        onClick={() => handleExport('png')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                    >
                        <FaDownload />
                        Exportar
                    </button>
                    <button
                        onClick={handleDuplicate}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                        <FaCopy />
                        Duplicar
                    </button>
                    <button
                        onClick={handleDelete}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                        <FaTrash />
                        Eliminar
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Toolbar Left */}
                <div className="w-16 bg-white border-r border-gray-200">
                    <Toolbar
                        onAddElement={async (type) => {
                            if (!currentInfografiaId || !canvasData) return;

                            // Generar ID único para el nuevo elemento
                            const newElementId = `elem-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                            // Calcular posición para el nuevo elemento (evitar superposiciones)
                            const existingPositions = canvasData.elementos.map(el => ({
                                x: el.posicion.x,
                                y: el.posicion.y
                            }));

                            // Buscar una posición libre (grid de 50px)
                            let newX = 100;
                            let newY = 100;
                            let foundPosition = false;

                            for (let y = 50; y < canvasData.dimensiones.alto && !foundPosition; y += 100) {
                                for (let x = 50; x < canvasData.dimensiones.ancho && !foundPosition; x += 200) {
                                    const isOccupied = existingPositions.some(pos =>
                                        Math.abs(pos.x - x) < 150 && Math.abs(pos.y - y) < 50
                                    );
                                    if (!isOccupied) {
                                        newX = x;
                                        newY = y;
                                        foundPosition = true;
                                    }
                                }
                            }

                            // Crear elemento según el tipo
                            let newElement = {
                                id: newElementId,
                                tipo: type,
                                posicion: { x: newX, y: newY },
                                dimensiones: { ancho: 200, alto: 100 },
                                rotacion: 0,
                                zIndex: canvasData.elementos.length,
                                propiedades: {}
                            };

                            // Propiedades por defecto según el tipo
                            switch (type) {
                                case 'texto':
                                    newElement.propiedades = {
                                        contenido: 'Nuevo texto',
                                        fuente: 'Arial',
                                        tamaño: 24,
                                        color: '#000000',
                                        alineacion: 'left',
                                        negrita: false,
                                        cursiva: false
                                    };
                                    break;
                                case 'qr':
                                    newElement.propiedades = {
                                        url: 'https://example.com',
                                        color: '#000000',
                                        colorFondo: '#FFFFFF',
                                        tamaño: 200
                                    };
                                    newElement.dimensiones = { ancho: 200, alto: 200 };
                                    break;
                                case 'formas':
                                    // Abrir selector de formas
                                    setPendingImageElement(newElement);
                                    setShowShapeSelector(true);
                                    return; // No añadir todavía, esperar selección
                                case 'icono':
                                    // Abrir selector de iconos
                                    setPendingImageElement(newElement);
                                    setShowIconSelector(true);
                                    return; // No añadir todavía, esperar selección
                                default:
                                    return;
                            }

                            // Añadir elemento al canvas
                            const updatedElements = [...canvasData.elementos, newElement];

                            setCanvasData({
                                ...canvasData,
                                elementos: updatedElements
                            });
                            setIsDirty(true);

                            // Seleccionar automáticamente el nuevo elemento para poder editarlo
                            // Para imágenes, esto permite subir la imagen inmediatamente
                            setTimeout(() => {
                                setSelectedElement(newElement);
                            }, 100);
                        }}
                    />
                </div>

                {/* Canvas Center */}
                <div className="flex-1 bg-gray-200 relative overflow-auto">
                    {/* Controles de Zoom */}
                    {canvasData && (
                        <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg p-2 flex items-center gap-2">
                            <button
                                onClick={() => {
                                    const newZoom = Math.min(currentZoom + 0.1, 2);
                                    setCurrentZoom(newZoom);
                                }}
                                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                title="Acercar"
                            >
                                +
                            </button>
                            <span className="text-sm text-gray-700 min-w-[80px] text-center">
                                {Math.round(currentZoom * 100)}%
                            </span>
                            <button
                                onClick={() => {
                                    const newZoom = Math.max(currentZoom - 0.1, 0.1);
                                    setCurrentZoom(newZoom);
                                }}
                                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                title="Alejar"
                            >
                                −
                            </button>
                            <button
                                onClick={() => {
                                    // Calcular zoom para que quepa en pantalla
                                    if (canvasData) {
                                        const containerWidth = window.innerWidth - 96 - 320; // Menos sidebar y panel props
                                        const containerHeight = window.innerHeight - 200; // Menos header
                                        const scaleX = containerWidth / canvasData.dimensiones.ancho;
                                        const scaleY = containerHeight / canvasData.dimensiones.alto;
                                        const fitZoom = Math.min(scaleX, scaleY, 1) * 0.85;
                                        setCurrentZoom(fitZoom);
                                    }
                                }}
                                className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs"
                                title="Ajustar a pantalla"
                            >
                                Fit
                            </button>
                        </div>
                    )}
                    {canvasData && (
                        <InfografiaCanvas
                            data={canvasData}
                            marca={marca}
                            onChange={handleCanvasChange}
                            onElementSelect={handleElementSelect}
                            selectedElement={selectedElement}
                            onZoomChange={setCurrentZoom}
                            externalZoom={currentZoom}
                            onCanvasReady={(canvasData) => {
                                setFabricCanvas(canvasData?.canvas || canvasData);
                                setPrepareForExport(() => canvasData?.prepareForExport || null);
                            }}
                        />
                    )}
                </div>

                {/* Properties Panel Right */}
                <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
                    {selectedElement ? (
                        <PropertiesPanel
                            element={selectedElement}
                            onUpdate={(updates) => {
                                handleElementUpdate(selectedElement.id, updates);
                            }}
                            onDelete={() => handleDeleteElement(selectedElement.id)}
                            marcaId={marcaId}
                            infografiaId={currentInfografiaId}
                            marca={marca}
                            onOpenImageSearch={selectedElement.tipo === 'imagen' ? () => {
                                setPendingImageElement(selectedElement);
                                setShowImageSearch(true);
                            } : undefined}
                            onOpenIconSelector={selectedElement.tipo === 'icono' ? () => {
                                setPendingImageElement(selectedElement);
                                setShowIconSelector(true);
                            } : undefined}
                        />
                    ) : (
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Propiedades del Canvas
                            </h3>

                            {/* Dimensiones Canvas */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Dimensiones del Canvas
                                </label>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-50 p-3 rounded-lg">
                                        <div className="text-sm font-medium text-gray-900">
                                            {canvasData?.dimensiones?.ancho} × {canvasData?.dimensiones?.alto} {canvasData?.dimensiones?.unidad || 'px'}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {canvasData?.dimensiones?.dpi && (
                                                <span className="mr-2">{canvasData.dimensiones.dpi} DPI</span>
                                            )}
                                            {canvasData?.dimensiones?.ancho === 2480 && canvasData?.dimensiones?.alto === 3508 && (
                                                <span className="text-blue-600 font-medium">• A4 (Impresión)</span>
                                            )}
                                            {canvasData?.dimensiones?.ancho === 1080 && canvasData?.dimensiones?.alto === 1920 && (
                                                <span className="text-purple-600 font-medium">• Instagram Story</span>
                                            )}
                                            {canvasData?.dimensiones?.ancho === 1080 && canvasData?.dimensiones?.alto === 1080 && (
                                                <span className="text-pink-600 font-medium">• Instagram Post</span>
                                            )}
                                            {canvasData?.dimensiones?.ancho === 1200 && canvasData?.dimensiones?.alto === 630 && (
                                                <span className="text-green-600 font-medium">• Facebook/LinkedIn</span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowDimensionSelector(true)}
                                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        title="Cambiar dimensiones"
                                    >
                                        <FaRulerCombined />
                                    </button>
                                </div>
                            </div>

                            <BackgroundProperties
                                fondo={canvasData?.fondo}
                                onUpdate={(newFondo) => {
                                    setCanvasData({
                                        ...canvasData,
                                        fondo: newFondo
                                    });
                                    setIsDirty(true);
                                }}
                                marcaId={marcaId}
                            />
                            <div className="mt-6 pt-6 border-t border-gray-200 text-center text-gray-500">
                                <p className="text-sm">Selecciona un elemento para editar sus propiedades</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Preview Modal */}
            {showPreview && canvasData && (
                <PreviewModal
                    canvasData={canvasData}
                    marca={marca}
                    onClose={() => setShowPreview(false)}
                    fabricCanvas={fabricCanvas}
                    prepareForExport={prepareForExport}
                />
            )}

            {/* Image Search Modal */}
            <ImageSearchModal
                isOpen={showImageSearch}
                onClose={() => {
                    setShowImageSearch(false);
                    setPendingImageElement(null);
                }}
                onSelectImage={(imageUrl) => {
                    if (pendingImageElement) {
                        // Si es un elemento nuevo, añadirlo
                        if (!pendingImageElement.id || !canvasData.elementos.find(el => el.id === pendingImageElement.id)) {
                            const newElement = {
                                ...pendingImageElement,
                                propiedades: {
                                    url: imageUrl,
                                    cdnUrl: imageUrl,
                                    opacidad: 1
                                }
                            };
                            const updatedElements = [...canvasData.elementos, newElement];
                            setCanvasData({
                                ...canvasData,
                                elementos: updatedElements
                            });
                        } else {
                            // Si es un elemento existente, actualizarlo
                            handleElementUpdate(pendingImageElement.id, {
                                propiedades: {
                                    ...pendingImageElement.propiedades,
                                    url: imageUrl,
                                    cdnUrl: imageUrl
                                }
                            });
                        }
                        setIsDirty(true);
                        setPendingImageElement(null);
                    }
                }}
            />

            {/* Icon Selector Modal */}
            <IconSelectorModal
                isOpen={showIconSelector}
                onClose={() => {
                    setShowIconSelector(false);
                    setPendingImageElement(null);
                }}
                onSelectIcon={(iconData) => {
                    if (pendingImageElement) {
                        // Verificar si es un elemento existente (tiene id y está en canvasData.elementos)
                        const existingElement = canvasData.elementos.find(el => el.id === pendingImageElement.id);

                        if (existingElement) {
                            // Actualizar elemento existente
                            handleElementUpdate(existingElement.id, {
                                propiedades: {
                                    ...existingElement.propiedades,
                                    nombre: iconData.name,
                                    libreria: iconData.library,
                                    prefijo: iconData.prefix
                                }
                            });
                        } else {
                            // Crear nuevo elemento de icono
                            const newElement = {
                                ...pendingImageElement,
                                tipo: 'icono',
                                propiedades: {
                                    nombre: iconData.name,
                                    libreria: iconData.library,
                                    prefijo: iconData.prefix,
                                    color: '#000000',
                                    tamaño: 100,
                                    opacidad: 1
                                },
                                dimensiones: { ancho: 100, alto: 100 }
                            };
                            const updatedElements = [...canvasData.elementos, newElement];
                            setCanvasData({
                                ...canvasData,
                                elementos: updatedElements
                            });
                            setIsDirty(true);

                            // Seleccionar el nuevo elemento automáticamente
                            setTimeout(() => {
                                setSelectedElement(newElement);
                            }, 100);
                        }

                        setPendingImageElement(null);
                    }
                }}
            />

            {/* Shape Selector Modal */}
            <ShapeSelectorModal
                isOpen={showShapeSelector}
                onClose={() => {
                    setShowShapeSelector(false);
                    setPendingImageElement(null);
                }}
                onSelectShape={(shapeData) => {
                    if (pendingImageElement) {
                        // Crear nuevo elemento de forma
                        const newElement = {
                            ...pendingImageElement,
                            tipo: 'forma',
                            propiedades: {
                                tipo: shapeData.id,
                                colorRelleno: '#3B82F6',
                                colorBorde: '#000000',
                                grosorBorde: 2,
                                opacidad: 1
                            },
                            dimensiones: {
                                ancho: shapeData.id === 'circulo' ? 150 : 200,
                                alto: shapeData.id === 'circulo' ? 150 : 150
                            }
                        };
                        const updatedElements = [...canvasData.elementos, newElement];
                        setCanvasData({
                            ...canvasData,
                            elementos: updatedElements
                        });
                        setIsDirty(true);

                        // Seleccionar el nuevo elemento automáticamente
                        setTimeout(() => {
                            setSelectedElement(newElement);
                        }, 100);

                        setPendingImageElement(null);
                    }
                }}
            />

            {/* Dimension Selector Modal */}
            <DimensionSelectorModal
                isOpen={showDimensionSelector}
                onClose={() => {
                    setShowDimensionSelector(false);
                    setSelectedTemplate(null); // Limpiar template seleccionado si cancela
                }}
                currentDimensions={canvasData?.dimensiones}
                onSelect={async (newDimensions) => {
                    // Si hay canvas existente, es un cambio de dimensiones
                    if (canvasData && currentInfografiaId) {
                        Swal.fire({
                            title: '¿Cambiar dimensiones del canvas?',
                            text: 'Esto podría afectar la posición de los elementos existentes. Se recomienda ajustarlos manualmente después.',
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: 'Sí, cambiar',
                            cancelButtonText: 'Cancelar'
                        }).then((result) => {
                            if (result.isConfirmed) {
                                setCanvasData({
                                    ...canvasData,
                                    dimensiones: newDimensions
                                });
                                setIsDirty(true);
                                Swal.fire({
                                    title: 'Dimensiones actualizadas',
                                    text: 'Revisa la posición de los elementos para asegurarte de que se ven correctamente.',
                                    icon: 'success',
                                    timer: 2000,
                                    showConfirmButton: false
                                });
                            }
                        });
                    } else {
                        // Si NO hay canvas, es un template "Personalizado" - crear nueva infografía
                        try {
                            const nuevaInfografia = {
                                marcaId,
                                nombre: `Infografía Personalizada`,
                                template: 'custom',
                                dimensiones: newDimensions,
                                elementos: [],
                                fondo: { tipo: 'color', valor: { color: '#FFFFFF' } },
                                coloresCorporativos: {
                                    primario: marca?.colorPrimario || '#3B82F6',
                                    secundario: marca?.colorSecundario || '#1D4ED8',
                                    texto: '#111827',
                                    fondo: '#FFFFFF'
                                },
                                esBorrador: true
                            };

                            const response = await infografiaService.createInfografia(nuevaInfografia);
                            const infografia = response?.infografia || response;
                            setCurrentInfografiaId(infografia._id);
                            setCanvasData({
                                elementos: [],
                                fondo: { tipo: 'color', valor: { color: '#FFFFFF' } },
                                dimensiones: newDimensions
                            });
                            setIsDirty(false);
                            setShowDimensionSelector(false);
                            setSelectedTemplate(null);
                        } catch (error) {
                            console.error('Error creando infografía personalizada:', error);
                            Swal.fire({
                                title: 'Error',
                                text: error.message || 'No se pudo crear la infografía',
                                icon: 'error'
                            });
                        }
                    }
                }}
            />
        </div>
    );
};

export default InfografiaGeneratorPage;

